import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { useTheme } from '../contexts/ThemeContext';
import { useSelectedAccount } from '../contexts/SelectedAccountContext';
import apiClient from '../services/api/apiClient';
import { useToast } from '../components/ui/Toast/Toast';
import { DropdownMenu } from '../components/ui/Button/Button';
import {
  ContactCardVisual,
  FamilyAvatarsVisual,
  ManualDataVisual,
  AIGenerateVisual,
  InstagramCommentsVisual
} from '../components/visuals/UnifiedBentoVisuals';

/**
 * MyAutomationsPage - 管理自动化项目
 * 支持：自动留言(Comments)、海巡(Topic Tracking)
 */
function MyAutomationsPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { toast } = useToast();
  const { accounts, selectedAccountId } = useSelectedAccount();

  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [triggerFilter, setTriggerFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState(null);

  useEffect(() => {
    loadAutomations();
  }, []);

  const loadAutomations = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/automations');
      if (response.data.success) {
        setAutomations(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to load automations:', error);
      setAutomations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAutomation = () => {
    setEditingAutomation(null); // 確保是新建模式
    setShowCreateModal(true);
  };

  const handleEditAutomation = async (automation) => {
    try {
      // 獲取完整的 automation 資料（包含 goals）
      const response = await apiClient.get(`/automations/${automation.id}`);
      if (response.data.success) {
        setEditingAutomation(response.data.data);
        setShowCreateModal(true);
      }
    } catch (error) {
      console.error('Failed to load automation:', error);
      toast.error('無法載入自動化資料');
    }
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setEditingAutomation(null);
    // 重置所有狀態將在下次打開時由 CreateAutomationModal 處理
  };

  const handleDeleteAutomation = async (id) => {
    if (!window.confirm('確定要刪除此自動化項目？')) return;

    try {
      await apiClient.delete(`/automations/${id}`);
      toast.success('自動化項目已刪除');
      loadAutomations();
    } catch (error) {
      console.error('Failed to delete automation:', error);
      toast.error('刪除失敗');
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await apiClient.put(`/automations/${id}/toggle`, {
        isActive: !currentStatus
      });
      toast.success(currentStatus ? '已暫停' : '已啟動');
      loadAutomations();
    } catch (error) {
      console.error('Failed to toggle status:', error);
      toast.error('操作失敗');
    }
  };

  const handleDuplicateAutomation = async (automation) => {
    try {
      const duplicateData = {
        name: `${automation.name} (Copy)`,
        type: automation.type,
        trigger: automation.trigger,
        action: automation.action
      };

      await apiClient.post('/automations', duplicateData);
      toast.success('自動化已複製');
      loadAutomations();
    } catch (error) {
      console.error('Failed to duplicate automation:', error);
      toast.error('複製失敗');
    }
  };

  const filteredAutomations = automations.filter(auto => {
    const matchSearch = auto.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchTrigger = triggerFilter === 'all' || auto.type === triggerFilter;
    const matchStatus = statusFilter === 'all' ||
      (statusFilter === 'live' && auto.isActive) ||
      (statusFilter === 'paused' && !auto.isActive);
    return matchSearch && matchTrigger && matchStatus;
  });
  const hasAutomations = automations.length > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex h-screen">
        <Sidebar activePage="automations" />

        <div className="flex-1 overflow-y-auto">
          {hasAutomations && (
            <>
              {/* Header */}
              <div className="bg-white border-b border-gray-200 px-8 py-6">
                <div className="flex items-center justify-between mb-6">
                  <h1 className="text-3xl font-bold text-gray-900">My Automations</h1>
                  <button
                    onClick={handleCreateAutomation}
                    className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    <Icon icon="mdi:plus" width={20} />
                    New Automation
                  </button>
                </div>

                {/* Search & Filters */}
                <div className="flex items-center gap-4">
                  {/* Search */}
                  <div className="flex-1 relative">
                    <Icon
                      icon="mdi:magnify"
                      width={20}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <input
                      type="text"
                      placeholder="Search all Automations"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Trigger Filter */}
                  <select
                    value={triggerFilter}
                    onChange={(e) => setTriggerFilter(e.target.value)}
                    className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-700"
                  >
                    <option value="all">Any Trigger</option>
                    <option value="comments">Comments</option>
                    <option value="topic-tracking">Topic Tracking</option>
                  </select>

                  {/* Status Filter */}
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-700"
                  >
                    <option value="all">Any Trigger states</option>
                    <option value="live">Live</option>
                    <option value="paused">Paused</option>
                  </select>
                </div>
              </div>

              {/* Actions Bar */}
              <div className="bg-white border-b border-gray-200 px-8 py-4">
                <div className="flex items-center justify-end">
                  {/* Right Actions */}
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => navigate('/trash')}
                      className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors"
                    >
                      <Icon icon="mdi:delete-outline" width={18} />
                      Trash
                    </button>

                    <div className="h-6 w-px bg-gray-300" />

                    <button
                      onClick={() => setViewMode(viewMode === 'list' ? 'grid' : 'list')}
                      className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors"
                    >
                      <Icon icon={viewMode === 'list' ? 'mdi:view-grid-outline' : 'mdi:view-list'} width={18} />
                      View as {viewMode === 'list' ? 'grid' : 'list'}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Automations List */}
          <div className={hasAutomations ? 'p-8' : 'px-6 py-12 min-h-[70vh] flex items-center justify-center'}>
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Icon icon="mdi:loading" width={48} className="text-blue-600 animate-spin" />
              </div>
            ) : !hasAutomations ? (
              <div className="w-full max-w-5xl">
                <div className="relative overflow-hidden rounded-[32px] border border-slate-200/70 bg-gradient-to-br from-white via-slate-50 to-blue-50 p-8 md:p-12 shadow-[0_30px_80px_-50px_rgba(15,23,42,0.6)]">
                  <div className="absolute -top-24 -right-16 h-56 w-56 rounded-full bg-gradient-to-br from-indigo-200/70 to-cyan-200/20 blur-3xl" />
                  <div className="absolute -bottom-24 -left-12 h-48 w-48 rounded-full bg-gradient-to-tr from-purple-200/60 to-pink-200/30 blur-3xl" />
                  <div
                    className="absolute inset-0 opacity-40"
                    style={{ backgroundImage: 'radial-gradient(circle at top right, rgba(59,130,246,0.12), transparent 55%)' }}
                  />

                  <div className="relative grid gap-8">
                    <div className="space-y-6">
                      <span className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-white">
                        Automation Studio
                      </span>
                      <h3
                        className="text-3xl md:text-4xl font-semibold text-slate-900 leading-tight"
                        style={{ fontFamily: "'Space Grotesk', 'Noto Sans TC', sans-serif" }}
                      >
                        打造你的第一個自動化劇本
                      </h3>
                      <p className="text-base text-slate-600">
                        將互動、追蹤、回覆整合成一條自動化流程，AI 會幫你完成 80% 的重複工作。
                      </p>

                      <div className="flex flex-wrap gap-3">
                        <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm">
                          <Icon icon="mdi:comment-processing-outline" width={16} className="text-indigo-600" />
                          智能留言回覆
                        </span>
                        <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm">
                          <Icon icon="mdi:compass-rose" width={16} className="text-emerald-600" />
                          熱門議題追蹤
                        </span>
                        <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm">
                          <Icon icon="mdi:chart-timeline-variant" width={16} className="text-sky-600" />
                          AI 自動化經營
                        </span>
                      </div>

                      <div className="rounded-2xl border border-white/70 bg-white/80 p-5 shadow-sm relative overflow-hidden">
                        <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                          <span>Automation Flow</span>
                          <span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] text-slate-500">Preview</span>
                        </div>
                        <div className="relative mt-5 h-44">
                          <svg className="absolute inset-0 h-full w-full" viewBox="0 0 420 176" fill="none">
                            <path d="M66 74H152V42H226" stroke="#E2E8F0" strokeWidth="2" strokeLinecap="round" />
                            <path d="M226 42H332" stroke="#E2E8F0" strokeWidth="2" strokeLinecap="round" />
                            <path d="M152 74V132H270" stroke="#E2E8F0" strokeWidth="2" strokeLinecap="round" />
                            <path d="M270 132V154H198" stroke="#E2E8F0" strokeWidth="2" strokeLinecap="round" />
                            <circle cx="152" cy="74" r="5" fill="#F1F5F9" stroke="#CBD5F5" strokeWidth="2" />
                            <circle cx="226" cy="42" r="5" fill="#F1F5F9" stroke="#CBD5F5" strokeWidth="2" />
                            <circle cx="270" cy="132" r="5" fill="#F1F5F9" stroke="#CBD5F5" strokeWidth="2" />
                            <circle cx="198" cy="154" r="5" fill="#F1F5F9" stroke="#CBD5F5" strokeWidth="2" />
                          </svg>

                          <div className="absolute left-4 top-12 rounded-full border border-slate-200 bg-white/90 px-4 py-2.5 text-[12px] font-semibold text-slate-700 shadow-sm">
                            <span className="mr-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-indigo-100 text-indigo-600">
                              <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M3 3h14a1 1 0 011 1v3a1 1 0 01-1 1H3a1 1 0 01-1-1V4a1 1 0 011-1zM2 10a1 1 0 011-1h14a1 1 0 011 1v6a1 1 0 01-1 1H3a1 1 0 01-1-1v-6z" />
                              </svg>
                            </span>
                            Import
                          </div>

                          <div className="absolute left-20 top-2 rounded-full border border-slate-200 bg-white/90 px-4 py-2.5 text-[12px] font-semibold text-slate-700 shadow-sm">
                            <span className="mr-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-purple-100 text-purple-600">
                              <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M4 3h12a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V4a1 1 0 011-1zM6 6h8v2H6V6zm0 4h8v2H6v-2z" />
                              </svg>
                            </span>
                            Create list
                          </div>

                          <div className="absolute right-6 top-6 rounded-full border border-slate-200 bg-white/90 px-4 py-2.5 text-[12px] font-semibold text-slate-700 shadow-sm">
                            <span className="mr-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-sky-100 text-sky-600">
                              <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M4 4h12a1 1 0 011 1v3H3V5a1 1 0 011-1zm-1 7h14v4a1 1 0 01-1 1H4a1 1 0 01-1-1v-4z" />
                              </svg>
                            </span>
                            Insert record
                          </div>

                          <div className="absolute right-12 top-20 rounded-full border border-slate-200 bg-white/90 px-4 py-2.5 text-[12px] font-semibold text-slate-700 shadow-sm">
                            <span className="mr-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                              <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M4 3h8l4 4v10a1 1 0 01-1 1H4a1 1 0 01-1-1V4a1 1 0 011-1z" />
                              </svg>
                            </span>
                            Store contacts
                          </div>

                          <div className="absolute left-28 bottom-2 rounded-full border border-slate-200 bg-white/90 px-4 py-2.5 text-[12px] font-semibold text-slate-700 shadow-sm">
                            <span className="mr-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                              <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M4 4h12a1 1 0 011 1v9a1 1 0 01-1 1H8l-4 3V5a1 1 0 010-1z" />
                              </svg>
                            </span>
                            Fetch mails
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-4">
                        <button
                          onClick={handleCreateAutomation}
                          className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-10 py-4 text-base font-semibold text-white shadow-xl shadow-slate-900/25 transition-all hover:-translate-y-1 hover:bg-slate-800"
                        >
                          <Icon icon="mdi:plus" width={20} />
                          立即建立
                        </button>
                        <div className="flex items-center gap-2 text-sm font-medium text-slate-500">
                          <span className="flex h-2.5 w-2.5 rounded-full bg-emerald-400 motion-safe:animate-pulse" />
                          2 分鐘完成設定
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200">
                {/* Table Header */}
                <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 border-b border-gray-200 text-sm font-medium text-gray-700">
                  <div className="col-span-1 flex items-center">
                    <input type="checkbox" className="w-4 h-4 text-blue-600 rounded border-gray-300" />
                  </div>
                  <div className="col-span-4">Name</div>
                  <div className="col-span-2 text-right">Runs</div>
                  <div className="col-span-2 text-right">CTR</div>
                  <div className="col-span-2 text-center">Account</div>
                  <div className="col-span-1 text-center">Status</div>
                </div>

                {/* Table Body */}
                <div className="divide-y divide-gray-100">
                  {filteredAutomations.length === 0 ? (
                    <div className="py-12 text-center text-sm text-gray-500">
                      No automations match your filters.
                    </div>
                  ) : (
                    filteredAutomations.map((automation) => (
                      <AutomationRow
                        key={automation.id}
                        automation={automation}
                        onDelete={() => handleDeleteAutomation(automation.id)}
                        onToggle={() => handleToggleStatus(automation.id, automation.isActive)}
                        onDuplicate={() => handleDuplicateAutomation(automation)}
                        onEdit={() => handleEditAutomation(automation)}
                      />
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create/Edit Automation Modal */}
      {showCreateModal && (
        <CreateAutomationModal
          editingAutomation={editingAutomation}
          onClose={handleCloseCreateModal}
          onSuccess={() => {
            handleCloseCreateModal();
            loadAutomations();
          }}
        />
      )}
    </div>
  );
}

/**
 * AutomationRow - 單個自動化項目行
 */
function AutomationRow({ automation, onDelete, onToggle, onDuplicate, onEdit }) {
  const [isToggling, setIsToggling] = useState(false);

  const handleToggle = async () => {
    setIsToggling(true);
    try {
      await onToggle();
    } finally {
      setIsToggling(false);
    }
  };

  const menuOptions = [
    {
      label: automation.isActive ? 'Pause' : 'Resume',
      icon: automation.isActive ? 'mdi:pause' : 'mdi:play',
      onClick: handleToggle
    },
    {
      label: 'Edit',
      icon: 'mdi:pencil',
      onClick: onEdit
    },
    {
      label: 'Duplicate',
      icon: 'mdi:content-duplicate',
      onClick: onDuplicate
    },
    { divider: true },
    {
      label: 'Delete',
      icon: 'mdi:delete',
      danger: true,
      onClick: onDelete
    }
  ];

  return (
    <div className="grid grid-cols-12 gap-4 px-6 py-4 hover:bg-gray-50 transition-colors group">
      {/* Checkbox */}
      <div className="col-span-1 flex items-center">
        <input type="checkbox" className="w-4 h-4 text-blue-600 rounded border-gray-300" />
      </div>

      {/* Name & Trigger */}
      <div className="col-span-4">
        <div className="flex items-center gap-3">
          {/* Status Badge */}
          {automation.isActive && (
            <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded uppercase">
              LIVE
            </span>
          )}
          {!automation.isActive && (
            <span className="px-2 py-0.5 bg-gray-400 text-white text-xs font-bold rounded uppercase">
              PAUSED
            </span>
          )}

          {/* Name */}
          <h3 className="text-base font-semibold text-gray-900">{automation.name}</h3>
        </div>

        {/* Trigger Info */}
        <div className="flex items-center gap-2 mt-2 text-sm text-gray-600">
          <Icon icon="mdi:instagram" width={16} className="text-pink-500" />
          <span>{automation.trigger}</span>
        </div>
      </div>

      {/* Runs */}
      <div className="col-span-2 flex items-center justify-end">
        <div className="text-right">
          <div className="text-base font-semibold text-gray-900">{automation.runs}</div>
          <div className="text-xs text-gray-500">runs</div>
        </div>
      </div>

      {/* CTR */}
      <div className="col-span-2 flex items-center justify-end">
        <div className="text-right">
          <div className="text-base font-semibold text-gray-900">{automation.ctr}%</div>
          <div className="text-xs text-gray-500">CTR</div>
        </div>
      </div>

      {/* Account */}
      <div className="col-span-2 flex items-center justify-center">
        <div className="flex items-center gap-2">
          <Icon icon="mdi:account-circle" width={18} className="text-gray-400" />
          <span className="text-sm text-gray-700 font-medium">
            {automation.accountName || 'N/A'}
          </span>
        </div>
      </div>

      {/* Status Toggle Switch */}
      <div className="col-span-1 flex items-center justify-center">
        <div className="flex items-center gap-3">
          {/* Toggle Switch */}
          <button
            onClick={handleToggle}
            disabled={isToggling}
            className={`
              relative inline-flex h-6 w-11 items-center rounded-full
              transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
              ${isToggling ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              ${automation.isActive ? 'bg-green-600' : 'bg-gray-300'}
            `}
            title={automation.isActive ? 'Click to pause' : 'Click to activate'}
          >
            <span
              className={`
                inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                ${automation.isActive ? 'translate-x-6' : 'translate-x-1'}
              `}
            />
          </button>

          {/* Actions Menu */}
          <DropdownMenu
            icon="mdi:dots-vertical"
            options={menuOptions}
            size="sm"
            align="right"
            triggerClassName="opacity-0 group-hover:opacity-100"
          />
        </div>
      </div>
    </div>
  );
}

/**
 * CreateAutomationModal - 創建/編輯自動化彈窗
 */
function CreateAutomationModal({ editingAutomation, onClose, onSuccess }) {
  const { toast } = useToast();
  const { accounts, selectedAccountId } = useSelectedAccount();
  const isEditMode = !!editingAutomation;
  const [step, setStep] = useState(isEditMode ? 2 : 1); // 編輯模式直接進入 Step 2
  const [automationType, setAutomationType] = useState(isEditMode ? editingAutomation.type : '');
  const [questionStep, setQuestionStep] = useState(0); // Comments 問卷步驟，從 0 開始
  const [topicTrackingQuestionStep, setTopicTrackingQuestionStep] = useState(0); // Topic Tracking 問卷步驟，從 0 開始
  const [createdAutomationId, setCreatedAutomationId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [userAccounts, setUserAccounts] = useState([]); // 用戶的 Threads 帳號列表
  const [publishedPosts, setPublishedPosts] = useState([]); // 已發布的貼文列表
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [selectedAutomationAccountId, setSelectedAutomationAccountId] = useState(selectedAccountId || null);
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    trigger: '',
    action: '',
    startLive: false,
    q0_post_binding: '', // 綁定的貼文 ID，或 'next' 表示下一篇文
    q0_next_post_content: '', // 下一篇文的內容（當選擇"下一篇文"時）
    q1_situation: '',
    q2_product_info: '',
    q3_target_audience: '',
    q4_features: '',
    q5_reply_method: '',
    q6_link: ''
  });

  const handleTypeSelect = (type) => {
    setAutomationType(type);
    setFormData({ ...formData, type });
    setQuestionStep(0); // 重置 Comments 問卷步驟，從 Q0 開始
    setTopicTrackingQuestionStep(0); // 重置 Topic Tracking 問卷步驟，從 0 開始
    setStep(2);
  };

  // 獲取用戶的 Threads 帳號和貼文
  useEffect(() => {
    const fetchPosts = async () => {
      if (automationType !== 'comments') return; // 只在 Comments 類型時需要

      try {
        // 1. 從 context 獲取 Threads 帳號列表
        const threadsAccounts = accounts.filter(
          acc => acc.platform === 'threads' && acc.status === 'active'
        );
        setUserAccounts(threadsAccounts);

        // 2. 如果有帳號，獲取第一個帳號的貼文列表（或當前選中的帳號）
        const targetAccountId = selectedAccountId || (threadsAccounts.length > 0 ? threadsAccounts[0].id : null);

        if (targetAccountId) {
          setLoadingPosts(true);
          const postsResponse = await apiClient.get(`/threads/insights/cached-posts?accountId=${targetAccountId}`);

          if (postsResponse.data.success && postsResponse.data.data) {
            const { posts } = postsResponse.data.data;
            // 過濾並格式化貼文
            const formattedPosts = (posts || [])
              .filter(post => post.media_type !== 'REPOST_FACADE')
              .map(post => ({
                id: post.post_id,
                text: post.content_text || 'No content',
                timestamp: post.post_timestamp,
                like_count: post.likes_count ?? post.like_count ?? 0,
                reply_count: post.replies_count ?? post.reply_count ?? 0,
                media_url: post.media_url
              }));
            setPublishedPosts(formattedPosts);
          }
        }
      } catch (error) {
        console.error('Failed to fetch posts:', error);
      } finally {
        setLoadingPosts(false);
      }
    };

    fetchPosts();
  }, [automationType, accounts, selectedAccountId]);

  // 當 modal 打開時初始化或預填資料
  useEffect(() => {
    if (isEditMode && editingAutomation) {
      // 編輯模式：預填資料
      const goals = editingAutomation.goals || {};
      const trigger = goals.trigger || {};
      const action = goals.action || {};

      setStep(2); // 直接進入問卷步驟
      setAutomationType(editingAutomation.type);
      setQuestionStep(0); // 編輯模式也從 Q0 開始
      setFormData({
        name: editingAutomation.name || '',
        type: editingAutomation.type,
        trigger,
        action,
        startLive: false,
        // 從 trigger 提取問卷資料 (Comments - Q0)
        q0_post_binding: trigger.post_binding || '',
        q0_next_post_content: trigger.next_post_content || '',
        // 從 trigger 提取問卷資料 (Comments - Q1-Q4)
        q1_situation: trigger.situation || '',
        q2_product_info: trigger.product_info || '',
        q3_target_audience: trigger.target_audience || '',
        q4_features: trigger.features || '',
        // 從 action 提取問卷資料 (Comments - Q5-Q6)
        q5_reply_method: action.reply_method || '',
        q6_link: action.link || '',
        // 從 trigger 提取關鍵字和模式 (Topic Tracking)
        keywords: trigger.keywords || [],
        trackingMode: trigger.mode || (trigger.keywords && trigger.keywords.length > 0 ? 'search' : 'create')
      });
    } else {
      // 新建模式：重置所有狀態
      setStep(1);
      setAutomationType('');
      setQuestionStep(0); // 從 Q0 開始
      setCreatedAutomationId(null);
      setIsCreating(false);
      setIsActivating(false);
      setFormData({
        name: '',
        type: '',
        trigger: '',
        action: '',
        startLive: false,
        q0_post_binding: '',
        q0_next_post_content: '',
        q1_situation: '',
        q2_product_info: '',
        q3_target_audience: '',
        q4_features: '',
        q5_reply_method: '',
        q6_link: '',
        keywords: [],
        trackingMode: 'search' // 預設為搜尋模式
      });
    }
  }, []); // 僅在 mount 時執行

  const handleCreate = async () => {
    // 為 Comments 自動生成名稱（如果沒有設置）
    let automationName = formData.name;
    if (!automationName && automationType === 'comments') {
      automationName = formData.q1_situation === 'engage'
        ? '輕鬆互動自動回覆'
        : formData.q2_product_info
          ? `${formData.q2_product_info.slice(0, 20)}自動回覆`
          : '自動留言回覆';
    }

    if (!automationName) {
      toast.error('請輸入自動化名稱');
      return;
    }

    // Topic Tracking 搜尋模式必須至少有一個關鍵字
    if (automationType === 'topic-tracking' && formData.trackingMode === 'search') {
      if (!formData.keywords || formData.keywords.length === 0) {
        toast.error('請至少新增一個追蹤關鍵字');
        return;
      }
    }

    if (isCreating) return; // 防止重複點擊

    try {
      setIsCreating(true);

      // 組裝 trigger 和 action 資料
      const trigger = automationType === 'comments' ? {
        type: 'comment',
        post_binding: formData.q0_post_binding || null,
        next_post_content: formData.q0_post_binding === 'next' ? formData.q0_next_post_content : null,
        situation: formData.q1_situation,
        product_info: formData.q2_product_info,
        target_audience: formData.q3_target_audience,
        features: formData.q4_features
      } : automationType === 'topic-tracking' ? {
        type: 'keyword',
        mode: formData.trackingMode || 'search',
        keywords: formData.trackingMode === 'search' ? (formData.keywords || []) : []
      } : {};

      const action = automationType === 'comments' ? {
        reply_method: formData.q5_reply_method,
        link: formData.q6_link
      } : automationType === 'topic-tracking' ? {
        type: 'track',
        notify: true
      } : {};

      if (isEditMode) {
        // 編輯模式：使用 PUT 更新
        const updateData = {
          name: automationName,
          trigger,
          action
        };
        console.log('Updating automation:', updateData);

        await apiClient.put(`/automations/${editingAutomation.id}`, updateData);

        toast.success('自動化已更新！');
        onSuccess(); // 直接關閉並刷新列表
      } else {
        // 新建模式：使用 POST 創建
        const createData = {
          name: automationName,
          type: formData.type,
          trigger,
          action,
          accountId: selectedAutomationAccountId || null
        };
        console.log('Creating automation:', createData);
        console.log('Current formData:', formData);

        const response = await apiClient.post('/automations', createData);

        const automationId = response.data.data.id;
        setCreatedAutomationId(automationId);

        toast.success('自動化項目已創建');

        // 更新 formData 中的 name（用於確認頁面顯示）
        setFormData(prev => ({ ...prev, name: automationName }));

        // 進入確認啟動步驟
        setStep(3);
      }
    } catch (error) {
      console.error('Failed to save automation:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      const errorMsg = error.response?.data?.error || error.message || '未知錯誤';
      toast.error(isEditMode ? `更新失敗: ${errorMsg}` : `創建失敗: ${errorMsg}`);
    } finally {
      setIsCreating(false);
    }
  };

  const handleActivate = async () => {
    if (isActivating) return; // 防止重複點擊

    try {
      setIsActivating(true);
      await apiClient.put(`/automations/${createdAutomationId}/toggle`, {
        isActive: true
      });
      toast.success('自動化已啟動！');
      onSuccess();
    } catch (error) {
      console.error('Failed to activate automation:', error);
      toast.error('啟動失敗');
    } finally {
      setIsActivating(false);
    }
  };

  const handleSkipActivation = () => {
    toast.info('您可以稍後在列表中啟動此自動化');
    onSuccess();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-[600px] h-[800px] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-2xl font-bold text-gray-900">
            {isEditMode && step === 2 && 'Edit Automation'}
            {!isEditMode && step === 1 && 'Create New Automation'}
            {!isEditMode && step === 2 && automationType === 'comments' && `Question ${questionStep + 1}`}
            {!isEditMode && step === 2 && automationType === 'topic-tracking' && 'Configure Topic Tracking'}
            {!isEditMode && step === 3 && 'Start Your Automation'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Icon icon="mdi:close" width={24} className="text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 1 ? (
            // Step 1: 選擇類型
            <div className="space-y-6">
              <p className="text-gray-600 mb-6">選擇自動化類型：</p>

              <div className="space-y-4">
                {/* Comments Automation - Bento Card */}
                <button
                  onClick={() => handleTypeSelect('comments')}
                  className="w-full relative overflow-hidden rounded-2xl border-2 border-gray-200 bg-gradient-to-br from-blue-50 to-white p-6 text-left transition-all hover:border-blue-500 hover:shadow-xl group"
                >
                  {/* Grid Background */}
                  <div
                    className="absolute inset-0 opacity-[0.02] pointer-events-none"
                    style={{
                      backgroundImage: 'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)',
                      backgroundSize: '20px 20px'
                    }}
                  />

                  {/* Decorative Elements */}
                  <div className="absolute -top-4 -right-4 h-24 w-24 rounded-full bg-blue-100 opacity-20 blur-2xl group-hover:opacity-30 transition-opacity" />

                  {/* Content */}
                  <div className="relative">
                    <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-600 shadow-lg group-hover:scale-110 transition-transform">
                      <Icon icon="mdi:message-reply-text" width={28} className="text-white" />
                    </div>

                    <h3 className="mb-2 text-lg font-bold text-gray-900">自動留言</h3>
                    <p className="mb-3 text-xs text-gray-600 leading-relaxed">自動回覆用戶留言、發送 DM、收集聯繫方式等</p>

                    {/* Features */}
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2 text-xs text-blue-700">
                        <Icon icon="mdi:check-circle" className="w-4 h-4" />
                        <span>智能回覆</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-blue-700">
                        <Icon icon="mdi:check-circle" className="w-4 h-4" />
                        <span>自動 DM</span>
                      </div>
                    </div>

                    {/* Arrow */}
                    <div className="absolute bottom-6 right-6">
                      <Icon icon="mdi:arrow-right" width={24} className="text-blue-600 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </button>

                {/* Topic Tracking Automation - Bento Card */}
                <button
                  onClick={() => handleTypeSelect('topic-tracking')}
                  className="w-full relative overflow-hidden rounded-2xl border-2 border-gray-200 bg-gradient-to-br from-purple-50 to-white p-6 text-left transition-all hover:border-purple-500 hover:shadow-xl group"
                >
                  {/* Grid Background */}
                  <div
                    className="absolute inset-0 opacity-[0.02] pointer-events-none"
                    style={{
                      backgroundImage: 'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)',
                      backgroundSize: '20px 20px'
                    }}
                  />

                  {/* Decorative Elements */}
                  <div className="absolute -top-4 -right-4 h-24 w-24 rounded-full bg-purple-100 opacity-20 blur-2xl group-hover:opacity-30 transition-opacity" />

                  {/* Content */}
                  <div className="relative">
                    <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-purple-600 shadow-lg group-hover:scale-110 transition-transform">
                      <Icon icon="mdi:compass-outline" width={28} className="text-white" />
                    </div>

                    <h3 className="mb-2 text-lg font-bold text-gray-900">海巡</h3>
                    <p className="mb-3 text-xs text-gray-600 leading-relaxed">監控關鍵字、自動互動、追蹤話題趨勢</p>

                    {/* Features */}
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2 text-xs text-purple-700">
                        <Icon icon="mdi:check-circle" className="w-4 h-4" />
                        <span>關鍵字監控</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-purple-700">
                        <Icon icon="mdi:check-circle" className="w-4 h-4" />
                        <span>趨勢追蹤</span>
                      </div>
                    </div>

                    {/* Arrow */}
                    <div className="absolute bottom-6 right-6">
                      <Icon icon="mdi:arrow-right" width={24} className="text-purple-600 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </button>
              </div>
            </div>
          ) : step === 2 ? (
            // Step 2: 配置
            automationType === 'comments' || (automationType === 'topic-tracking' && questionStep >= 2) ? (
              // Comments 問卷流程
              <CommentsQuestionnaire
                questionStep={questionStep}
                setQuestionStep={setQuestionStep}
                formData={formData}
                setFormData={setFormData}
                onBack={() => setStep(1)}
                onCreate={handleCreate}
                isCreating={isCreating}
                isEditMode={isEditMode}
                publishedPosts={publishedPosts}
                loadingPosts={loadingPosts}
                selectedAutomationAccountId={selectedAutomationAccountId}
                setSelectedAutomationAccountId={setSelectedAutomationAccountId}
                accounts={accounts}
              />
            ) : (
              // Topic Tracking 簡易配置
              <div className="space-y-6">
                {/* 帳號選擇器 */}
                {!isEditMode && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      選擇帳號 *
                    </label>
                    <select
                      value={selectedAutomationAccountId || ''}
                      onChange={(e) => {
                        const accountId = e.target.value;
                        setSelectedAutomationAccountId(accountId);
                        if (accountId) {
                          const selectedAccount = accounts.find(acc => acc.id === accountId);
                          if (selectedAccount) {
                            toast.success(`已切換至帳號：${selectedAccount.account_name}`);
                          }
                        }
                      }}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-700"
                    >
                      <option value="">選擇一個帳號</option>
                      {accounts
                        .filter(acc => acc.platform === 'threads' && acc.status === 'active')
                        .map(acc => (
                          <option key={acc.id} value={acc.id}>
                            {acc.account_name} (@{acc.platform})
                          </option>
                        ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    自動化名稱 *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="例如：追蹤AI話題"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* 追蹤關鍵字 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    追蹤關鍵字 * <span className="text-xs text-gray-500 font-normal">
                      ({(formData.keywords || []).length}/3)
                    </span>
                  </label>
                  <div className="space-y-3">
                    <input
                      type="text"
                      placeholder={(formData.keywords || []).length >= 3 ? "已達到最多 3 個關鍵字" : "輸入關鍵字後按 Enter"}
                      disabled={(formData.keywords || []).length >= 3}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.target.value.trim()) {
                          e.preventDefault();
                          const newKeyword = e.target.value.trim();
                          const currentKeywords = formData.keywords || [];

                          // 檢查是否已達到最大數量
                          if (currentKeywords.length >= 3) {
                            toast.warning('最多只能追蹤 3 個關鍵字');
                            return;
                          }

                          // 檢查是否重複
                          if (currentKeywords.includes(newKeyword)) {
                            toast.warning('此關鍵字已存在');
                            return;
                          }

                          setFormData({
                            ...formData,
                            keywords: [...currentKeywords, newKeyword]
                          });
                          e.target.value = '';
                        }
                      }}
                    />

                    {/* 關鍵字標籤 */}
                    {formData.keywords && formData.keywords.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {formData.keywords.map((keyword, index) => (
                          <div
                            key={index}
                            className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-100 text-purple-700 rounded-full text-sm"
                          >
                            <Icon icon="mdi:tag" className="w-4 h-4" />
                            <span>{keyword}</span>
                            <button
                              onClick={() => {
                                const newKeywords = formData.keywords.filter((_, i) => i !== index);
                                setFormData({ ...formData, keywords: newKeywords });
                              }}
                              className="hover:text-purple-900 transition-colors"
                            >
                              <Icon icon="mdi:close" className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <p className="text-xs text-gray-500">
                      輸入關鍵字後按 Enter 新增，最多 3 個關鍵字。建議中文內容用中文關鍵字，點擊標籤上的 ✕ 刪除。
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 pt-4">
                  {!isEditMode && (
                    <button
                      onClick={() => setStep(1)}
                      disabled={isCreating}
                      className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      返回
                    </button>
                  )}
                  <button
                    onClick={() => {
                      // 驗證輸入
                      if (!selectedAutomationAccountId && !isEditMode) {
                        toast.error('請選擇一個帳號');
                        return;
                      }
                      if (!formData.name.trim()) {
                        toast.error('請輸入自動化名稱');
                        return;
                      }
                      if (!formData.keywords || formData.keywords.length === 0) {
                        toast.error('請至少新增一個追蹤關鍵字');
                        return;
                      }

                      setQuestionStep(2);
                    }}
                    disabled={isCreating}
                    className="flex-1 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isEditMode ? '更新自動化' : '下一步設定回覆內容'}
                  </button>
                </div>
              </div>
            )
          ) : (
            // Step 3: 確認啟動
            <div className="space-y-6">
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon icon="mdi:check-circle" width={48} className="text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">自動化創建成功！</h3>
                <p className="text-gray-600 mb-6">您的 <span className="font-semibold">{formData.name}</span> 已經準備就緒</p>

                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon icon="mdi:rocket-launch" width={24} className="text-white" />
                    </div>
                    <div className="flex-1 text-left">
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">要立即啟動嗎？</h4>
                      <p className="text-sm text-gray-700">啟動後，此自動化將立即開始運行並處理符合條件的觸發事件。</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={handleSkipActivation}
                  disabled={isActivating}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  稍後啟動
                </button>
                <button
                  onClick={handleActivate}
                  disabled={isActivating}
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isActivating ? (
                    <>
                      <Icon icon="mdi:loading" width={20} className="animate-spin" />
                      啟動中...
                    </>
                  ) : (
                    <>
                      <Icon icon="mdi:play" width={20} />
                      立即啟動 (Go Live)
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * CommentsQuestionnaire - Comments 自動化問卷流程
 */
function CommentsQuestionnaire({
  questionStep,
  setQuestionStep,
  formData,
  setFormData,
  onBack,
  onCreate,
  isCreating,
  isEditMode,
  publishedPosts,
  loadingPosts,
  selectedAutomationAccountId,
  setSelectedAutomationAccountId,
  accounts
}) {
  const { toast } = useToast();

  const handleNext = () => {
    // Q0 驗證
    if (questionStep === 0 && !isEditMode && !selectedAutomationAccountId) {
      toast.error('請選擇一個帳號');
      return;
    }
    if (questionStep === 0 && (!formData.q0_post_binding || formData.q0_post_binding === 'published')) {
      toast.error('請選擇要回覆的文章');
      return;
    }
    if (questionStep === 0 && formData.q0_post_binding === 'next' && !formData.q0_next_post_content.trim()) {
      toast.error('請輸入下一篇文的內容或描述');
      return;
    }

    // Q1 驗證
    if (questionStep === 1 && !formData.q1_situation) {
      toast.error('請選擇一個選項');
      return;
    }

    // 如果 Q1 選擇「想要增加互動輕鬆回應」，跳到 Q6 (回覆留言)
    if (questionStep === 1 && formData.q1_situation === 'engage') {
      setQuestionStep(6);
      return;
    }

    // 如果 Q1 選擇「有特定活動、產品、服務」，進入 Q2
    if (questionStep === 1 && formData.q1_situation === 'product') {
      setQuestionStep(2);
      return;
    }

    // Q2 驗證
    if (questionStep === 2 && !formData.q2_product_info) {
      toast.error('請填寫產品資訊');
      return;
    }

    // product 路線到 Q2 即結束，避免再往下走
    if (questionStep === 2 && formData.q1_situation === 'product') {
      return;
    }

    // 其他流程直接跳下一題
    if (questionStep < 6) {
      setQuestionStep(questionStep + 1);
    }
  };

  const handlePrevious = () => {
    // 從 Q6 回到 Q1（如果之前選的是「輕鬆回應」）
    if (questionStep === 6 && formData.q1_situation === 'engage') {
      setQuestionStep(1);
      return;
    }

    // 從 Q6 回到 Q5（如果是 product 路線）
    if (questionStep === 6 && formData.q1_situation === 'product') {
      setQuestionStep(5);
      return;
    }

    // 從 Q2 回到 Q1
    if (questionStep === 2) {
      setQuestionStep(1);
      return;
    }

    // 從 Q1 回到 Q0
    if (questionStep === 1) {
      setQuestionStep(0);
      return;
    }

    // 其他情況正常回上一題
    if (questionStep > 1) {
      setQuestionStep(questionStep - 1);
    }
  };

  const handleSubmit = () => {
    const requiresReplyMethod = formData.q1_situation === 'engage';
    if (requiresReplyMethod && !formData.q5_reply_method) {
      toast.error('請填寫回覆方式');
      return;
    }

    // 直接調用 onCreate，名稱生成已在 handleCreate 中處理
    onCreate();
  };

  return (
    <div className="space-y-6">
      {/* 編輯模式：顯示名稱編輯框（在 Q1 之前） */}
      {isEditMode && questionStep === 1 && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            自動化名稱
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="輸入自動化名稱"
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      )}

      {/* Q0: 選擇要回覆的文章 */}
      {questionStep === 0 && (
        <div className="space-y-6">
          {/* 帳號選擇器 (只在非編輯模式顯示) */}
          {!isEditMode && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                選擇帳號 *
              </label>
              <select
                value={selectedAutomationAccountId || ''}
                onChange={(e) => {
                  const accountId = e.target.value;
                  setSelectedAutomationAccountId(accountId);
                  if (accountId) {
                    const selectedAccount = accounts.find(acc => acc.id === accountId);
                    if (selectedAccount) {
                      toast.success(`已切換至帳號：${selectedAccount.account_name}`);
                    }
                  }
                }}
                className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-700"
              >
                <option value="">選擇一個帳號</option>
                {accounts
                  .filter(acc => acc.platform === 'threads' && acc.status === 'active')
                  .map(acc => (
                    <option key={acc.id} value={acc.id}>
                      {acc.account_name}
                    </option>
                  ))}
              </select>
              <p className="text-xs text-gray-500 mt-2">
                此自動化將綁定到選擇的帳號
              </p>
            </div>
          )}

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">選擇要回覆的文章</h3>
            <p className="text-sm text-gray-600 mb-6">
              選擇一篇已發布的貼文，或描述下一篇即將發布的內容
            </p>
          </div>

          {/* 選項 1: 已發布的貼文 */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <input
                type="radio"
                id="post-published"
                name="post_binding"
                checked={formData.q0_post_binding && formData.q0_post_binding !== 'next'}
                onChange={() => {
                  // 設定為 'published' 佔位符，顯示下拉選單讓用戶選擇
                  setFormData({ ...formData, q0_post_binding: 'published', q0_next_post_content: '' });
                }}
                className="w-4 h-4 text-blue-600"
              />
              <label htmlFor="post-published" className="text-sm font-medium text-gray-900">
                已發布的貼文
              </label>
            </div>

            {/* 貼文下拉選單 */}
            {formData.q0_post_binding && formData.q0_post_binding !== 'next' && (
              <div className="ml-6">
                {loadingPosts ? (
                  <div className="flex items-center gap-2 py-3 px-4 bg-gray-50 rounded-lg">
                    <Icon icon="mdi:loading" width={20} className="text-blue-500 animate-spin" />
                    <span className="text-sm text-gray-500">載入貼文中...</span>
                  </div>
                ) : publishedPosts.length === 0 ? (
                  <div className="py-4 px-4 bg-gray-50 rounded-lg text-center">
                    <Icon icon="mdi:inbox" width={32} className="text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">尚無已發布的貼文</p>
                  </div>
                ) : (
                  <select
                    value={formData.q0_post_binding === 'published' ? '' : formData.q0_post_binding}
                    onChange={(e) => setFormData({ ...formData, q0_post_binding: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 bg-white text-sm"
                  >
                    <option value="">選擇一篇貼文</option>
                    {publishedPosts.map((post) => {
                      const postText = post.text || '(無內容)';
                      const truncatedText = postText.length > 60 ? postText.substring(0, 60) + '...' : postText;
                      const postDate = new Date(post.timestamp).toLocaleDateString();
                      return (
                        <option key={post.id} value={post.id}>
                          {truncatedText} - {postDate}
                        </option>
                      );
                    })}
                  </select>
                )}
              </div>
            )}
          </div>

          {/* 選項 2: 下一篇文 */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <input
                type="radio"
                id="post-next"
                name="post_binding"
                checked={formData.q0_post_binding === 'next'}
                onChange={() => setFormData({ ...formData, q0_post_binding: 'next' })}
                className="w-4 h-4 text-purple-600"
              />
              <label htmlFor="post-next" className="text-sm font-medium text-gray-900">
                下一篇文（即將發布）
              </label>
            </div>

            {/* 下一篇文內容輸入 */}
            {formData.q0_post_binding === 'next' && (
              <div className="ml-6">
                <textarea
                  value={formData.q0_next_post_content}
                  onChange={(e) => setFormData({ ...formData, q0_next_post_content: e.target.value })}
                  placeholder="請描述下一篇文的內容或主題，例如：&#10;&#10;- 我會發一篇關於 AI 工具推薦的貼文&#10;- 下一篇會介紹我們的新產品功能&#10;- 即將分享一個行銷案例研究"
                  rows={6}
                  className="w-full px-4 py-3 border-2 border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none placeholder:text-gray-400"
                />
                <p className="text-xs text-gray-500 mt-2">
                  AI 將根據這個描述來理解並回覆留言
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Q1: 本次回覆你的情況是 */}
      {questionStep === 1 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">本次回覆你的情況是：</h3>

          <div className="grid grid-cols-1 gap-4">
            {/* Option 1: 輕鬆互動 */}
            <button
              onClick={() => setFormData({ ...formData, q1_situation: 'engage' })}
              className={`relative p-6 rounded-2xl text-left transition-all overflow-hidden group ${
                formData.q1_situation === 'engage'
                  ? 'border-2 border-blue-500 bg-gradient-to-br from-blue-50 to-white shadow-lg'
                  : 'border-2 border-gray-200 bg-white hover:border-blue-300 hover:shadow-md'
              }`}
            >
              {/* Background decoration */}
              <div className="absolute top-4 right-4 w-32 h-32 bg-blue-100 rounded-full opacity-20 blur-2xl"></div>

              {/* Tag */}
              <div className="relative mb-4">
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-500 text-white text-xs font-semibold rounded-full">
                  <Icon icon="mdi:chat-processing" width={14} />
                  輕鬆互動
                </span>
              </div>

              {/* Visual Elements */}
              <div className="relative mb-4 h-24 flex items-center justify-center">
                <div className="flex items-center gap-2">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform">
                    <Icon icon="mdi:emoticon-happy" width={24} className="text-white" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <div className="flex gap-1">
                      <div className="px-2 py-1 bg-gray-700 text-white text-xs rounded-md flex items-center gap-1">
                        <Icon icon="mdi:thumb-up" width={12} />
                        Like
                      </div>
                      <div className="px-2 py-1 bg-gray-700 text-white text-xs rounded-md flex items-center gap-1">
                        <Icon icon="mdi:message" width={12} />
                        Reply
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <div className="px-2 py-1 bg-gray-700 text-white text-xs rounded-md flex items-center gap-1">
                        <Icon icon="mdi:share" width={12} />
                        Share
                      </div>
                      <div className="px-2 py-1 bg-gray-700 text-white text-xs rounded-md flex items-center gap-1">
                        <Icon icon="mdi:heart" width={12} />
                        Love
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Title and Description */}
              <div className="relative">
                <h4 className="text-base font-bold text-gray-900 mb-2">
                  快速互動回應
                </h4>
                <p className="text-xs text-gray-600 leading-relaxed">
                  自動回應用戶留言，提升社群互動率與參與度
                </p>
              </div>

              {/* Check indicator */}
              {formData.q1_situation === 'engage' && (
                <div className="absolute top-3 right-3">
                  <Icon icon="mdi:check-circle" width={24} className="text-blue-600" />
                </div>
              )}
            </button>

            {/* Option 2: 產品推廣 */}
            <button
              onClick={() => setFormData({ ...formData, q1_situation: 'product' })}
              className={`relative p-6 rounded-2xl text-left transition-all overflow-hidden group ${
                formData.q1_situation === 'product'
                  ? 'border-2 border-purple-500 bg-gradient-to-br from-purple-50 to-white shadow-lg'
                  : 'border-2 border-gray-200 bg-white hover:border-purple-300 hover:shadow-md'
              }`}
            >
              {/* Background decoration */}
              <div className="absolute top-4 right-4 w-32 h-32 bg-purple-100 rounded-full opacity-20 blur-2xl"></div>

              {/* Tag */}
              <div className="relative mb-4">
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-500 text-white text-xs font-semibold rounded-full">
                  <Icon icon="mdi:bullhorn" width={14} />
                  產品推廣
                </span>
              </div>

              {/* Visual Elements - Product Grid */}
              <div className="relative mb-4 h-24 flex items-center justify-center">
                <div className="grid grid-cols-4 gap-1">
                  {[...Array(12)].map((_, i) => (
                    <div
                      key={i}
                      className={`w-6 h-6 rounded-full flex items-center justify-center ${
                        i === 6
                          ? 'bg-gradient-to-br from-purple-500 to-purple-600 ring-2 ring-purple-300 shadow-lg transform scale-110'
                          : i % 3 === 0
                          ? 'bg-purple-200'
                          : 'bg-gray-200'
                      } transform group-hover:scale-105 transition-transform`}
                      style={{ transitionDelay: `${i * 20}ms` }}
                    >
                      {i === 6 && (
                        <Icon icon="mdi:package-variant" width={14} className="text-white" />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Title and Description */}
              <div className="relative">
                <h4 className="text-base font-bold text-gray-900 mb-2">
                  活動產品推廣
                </h4>
                <p className="text-xs text-gray-600 leading-relaxed">
                  推廣特定活動、產品或服務，精準傳達行銷訊息
                </p>
              </div>

              {/* Check indicator */}
              {formData.q1_situation === 'product' && (
                <div className="absolute top-3 right-3">
                  <Icon icon="mdi:check-circle" width={24} className="text-purple-600" />
                </div>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Q2: 說明主推活動或產品資訊 */}
      {questionStep === 2 && (
        <div className="space-y-4">
          {/* Visual Card - Bento ContactCardVisual */}
          <ContactCardVisual />

          <h3 className="text-base font-bold text-gray-900 mb-2">說明主推活動或產品資訊</h3>
          <p className="text-xs text-gray-600 mb-3">描述你的產品、活動或服務，讓 AI 了解如何精準推廣</p>

          {/* Input */}
          <textarea
            value={formData.q2_product_info}
            onChange={(e) => setFormData({ ...formData, q2_product_info: e.target.value })}
            placeholder="例如：貼文的留言可以結合AI自動留言回覆，把所有潛在客戶再行銷、推廣活動、產品、服務…"
            rows={4}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
          />
        </div>
      )}

      {/* Q3: 你的目標族群是誰 (optional) */}
      {questionStep === 3 && (
        <div className="space-y-4">
          {/* Visual Card - Bento FamilyAvatarsVisual */}
          <FamilyAvatarsVisual />

          <h3 className="text-base font-bold text-gray-900 mb-2">
            你的目標族群是誰？ <span className="text-sm text-gray-500 font-normal">(選填)</span>
          </h3>
          <p className="text-xs text-gray-600 mb-3">描述你的理想客戶或受眾特徵</p>

          {/* Input */}
          <textarea
            value={formData.q3_target_audience}
            onChange={(e) => setFormData({ ...formData, q3_target_audience: e.target.value })}
            placeholder="例如：適合所有想透過Threads社群媒體行銷的品牌主、企業主行銷團隊與想變現的創作者們"
            rows={4}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
          />
        </div>
      )}

      {/* Q4: 特色是什麼 (optional) */}
      {questionStep === 4 && (
        <div className="space-y-4">
          {/* Visual Card - Bento ManualDataVisual */}
          <ManualDataVisual />

          <h3 className="text-base font-bold text-gray-900 mb-2">
            特色是什麼？ <span className="text-sm text-gray-500 font-normal">(選填)</span>
          </h3>
          <p className="text-xs text-gray-600 mb-3">說明你的產品或服務的獨特優勢</p>

          {/* Input */}
          <textarea
            value={formData.q4_features}
            onChange={(e) => setFormData({ ...formData, q4_features: e.target.value })}
            placeholder="例如：不用手動去找到想行銷的客群，可以跟所有潛在客戶去使用AI行銷，達到高效率、自動化且規模化的效果"
            rows={4}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
          />
        </div>
      )}

      {/* Q5: 想置入的連結 (僅 product 路線) */}
      {questionStep === 5 && (
        <div className="space-y-4">
          {/* Visual Card - Bento AIGenerateVisual */}
          <AIGenerateVisual />

          <h3 className="text-base font-bold text-gray-900 mb-2">
            想置入的連結？ <span className="text-sm text-gray-500 font-normal">(選填)</span>
          </h3>
          <p className="text-xs text-gray-600 mb-3">提供官網、活動頁或產品連結讓客戶了解更多</p>

          {/* Input */}
          <div className="relative">
            <Icon icon="mdi:link" width={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="url"
              value={formData.q6_link}
              onChange={(e) => setFormData({ ...formData, q6_link: e.target.value })}
              placeholder="https://yourwebsite.com/promotion"
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
          </div>
        </div>
      )}
      {/* Q6: 你想要怎麼回覆留言 */}
      {questionStep === 6 && (
        <div className="space-y-4">
          {/* Visual Card - Bento InstagramCommentsVisual */}
          <InstagramCommentsVisual />

          <h3 className="text-base font-bold text-gray-900 mb-2">你想要怎麼回覆留言？</h3>
          <p className="text-xs text-gray-600 mb-3">描述你希望 AI 如何回應用戶留言（引導、感謝、邀請等）</p>

          {/* Input */}
          <textarea
            value={formData.q5_reply_method}
            onChange={(e) => setFormData({ ...formData, q5_reply_method: e.target.value })}
            placeholder="例如：感謝留言並引導到官網、提供優惠碼、邀請加入社群..."
            rows={4}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
          />
        </div>
      )}

      {/* 導航按鈕 */}
      <div className="flex items-center gap-3 pt-4">
        {/* 只在非編輯模式或不是第一題時顯示返回/上一題按鈕 */}
        {(!isEditMode || questionStep > 0) && (
          <button
            onClick={questionStep === 0 ? onBack : handlePrevious}
            disabled={isCreating}
            className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {questionStep === 0 ? '返回' : '上一題'}
          </button>
        )}

        {/* 判斷是否為最後一題 */}
        {(() => {
          const isLastQuestion =
            (questionStep === 6 && formData.q1_situation === 'engage') || // engage 路線，Q6 是最後一題
            (questionStep === 2 && formData.q1_situation === 'product'); // product 路線，Q2 是最後一題

          return isLastQuestion ? (
            <button
              onClick={handleSubmit}
              disabled={isCreating}
              className="flex-1 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isCreating && <Icon icon="mdi:loading" width={20} className="animate-spin" />}
              {isCreating ? (isEditMode ? '更新中...' : '創建中...') : (isEditMode ? '更新自動化' : '創建自動化')}
            </button>
          ) : (
            <button
              onClick={handleNext}
              disabled={isCreating}
              className="flex-1 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              下一題
            </button>
          );
        })()}
      </div>

      {/* 進度指示器 */}
      <div className="flex items-center justify-center gap-2 pt-2">
        {[0, 1, 2, 3, 4, 5, 6].map((step) => {
          // 根據 Q1 選擇決定要顯示哪些步驟
          const shouldShow = step === 0 || ( // Q0 總是顯示
            formData.q1_situation === 'engage'
              ? (step === 1 || step === 6) // engage 路線：Q0, Q1, Q6
              : formData.q1_situation === 'product'
              ? step <= 2 // product 路線：Q0, Q1, Q2
              : step <= 6 // 未選擇時顯示 0-6
          );

          if (!shouldShow) return null;

          return (
            <div
              key={step}
              className={`h-2 rounded-full transition-all ${
                step === questionStep
                  ? 'w-8 bg-blue-600'
                  : step < questionStep
                  ? 'w-2 bg-blue-400'
                  : 'w-2 bg-gray-300'
              }`}
            />
          );
        })}
      </div>
    </div>
  );
}

export default MyAutomationsPage;
